
import React, { useState } from 'react';
import { PRICING_PLANS } from '../constants';
import { PlanType } from '../types';

const Pricing: React.FC = () => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('yearly');

  return (
    <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight sm:text-5xl">Plans designed for performance.</h2>
        <p className="mt-4 text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">Scaling from solo projects to full agency automation workflows.</p>
        
        <div className="mt-10 flex justify-center">
          <div className="relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-full p-1 flex items-center shadow-sm">
            <button 
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-bold transition-all ${billingPeriod === 'monthly' ? 'bg-indigo-600 text-white shadow-xl' : 'text-slate-500 dark:text-slate-400'}`}
            >
              Monthly
            </button>
            <button 
              onClick={() => setBillingPeriod('yearly')}
              className={`px-6 py-2 rounded-full text-sm font-bold transition-all ${billingPeriod === 'yearly' ? 'bg-indigo-600 text-white shadow-xl' : 'text-slate-500 dark:text-slate-400'}`}
            >
              Yearly <span className="text-[10px] ml-1 opacity-70">(Save 25%)</span>
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {(Object.keys(PRICING_PLANS) as PlanType[]).map((planId) => {
          const plan = PRICING_PLANS[planId];
          const price = billingPeriod === 'monthly' ? plan.priceMonthly : plan.priceYearly;

          return (
            <div key={planId} className={`relative bg-white dark:bg-slate-950/50 border flex flex-col rounded-3xl p-8 transition-all ${planId === PlanType.PRO ? 'border-indigo-500 bg-indigo-50/30 dark:bg-indigo-900/10 scale-105 shadow-2xl shadow-indigo-500/10' : 'border-slate-200 dark:border-slate-800 shadow-sm'}`}>
              {planId === PlanType.PRO && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-indigo-600 text-white text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest shadow-lg">Most Popular</div>
              )}
              
              <div className="mb-8">
                <h3 className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-4">{plan.id}</h3>
                <div className="flex items-baseline text-slate-900 dark:text-white">
                  <span className="text-5xl font-black tracking-tight">${price}</span>
                  <span className="ml-1 text-xl font-bold text-slate-400">/mo</span>
                </div>
                <p className="mt-2 text-xs font-bold text-slate-500">Billed {billingPeriod}.</p>
              </div>

              <ul className="mb-8 space-y-4 flex-1">
                <li className="flex items-center text-sm font-medium text-slate-600 dark:text-slate-300">
                  <span className="text-indigo-500 dark:text-indigo-400 mr-3 font-bold text-lg">✓</span> {plan.credits.toLocaleString()} Credits / mo
                </li>
                <li className="flex items-center text-sm font-medium text-slate-600 dark:text-slate-300">
                  <span className="text-indigo-500 dark:text-indigo-400 mr-3 font-bold text-lg">✓</span> {plan.bots} Active Bot{plan.bots > 1 ? 's' : ''}
                </li>
                <li className="flex items-center text-sm font-medium text-slate-600 dark:text-slate-300">
                  <span className="text-indigo-500 dark:text-indigo-400 mr-3 font-bold text-lg">✓</span> {plan.storageGb}GB Storage
                </li>
                <li className="flex items-center text-sm font-medium text-slate-600 dark:text-slate-300">
                   <span className={plan.ipRotation ? 'text-emerald-500 mr-3 font-bold text-lg' : 'text-slate-300 dark:text-slate-700 mr-3 font-bold text-lg'}>{plan.ipRotation ? '✓' : '✕'}</span> 
                   <span className={plan.ipRotation ? '' : 'text-slate-400 dark:text-slate-600'}>IP Rotation & CAPTCHA</span>
                </li>
                <li className="flex items-center text-sm font-medium text-slate-600 dark:text-slate-300">
                  <span className="text-indigo-500 dark:text-indigo-400 mr-3 font-bold text-lg">✓</span> {plan.support} Support
                </li>
              </ul>

              <button className={`w-full py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${planId === PlanType.PRO ? 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-xl shadow-indigo-600/20' : 'bg-slate-900 dark:bg-white text-white dark:text-slate-900'}`}>
                {planId === PlanType.FREE ? 'Get Started' : 'Upgrade Plan'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Add-ons Section */}
      <div className="mt-24">
        <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-8 text-center uppercase tracking-tight">Ecosystem Add-Ons</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { name: 'Extra Storage', price: '$10', desc: 'Per additional 10 GB' },
            { name: 'Additional Bot', price: '$50', desc: 'Per bot instance' },
            { name: 'Extra Credits', price: '$25', desc: 'Per 10,000 credits' },
            { name: 'Dedicated Support', price: '$99', desc: '24/7 Priority access' }
          ].map((addon, idx) => (
            <div key={idx} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-2xl flex items-center justify-between shadow-sm">
              <div>
                <div className="font-black text-slate-900 dark:text-white text-sm">{addon.name}</div>
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">{addon.desc}</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-black text-indigo-600 dark:text-indigo-400">{addon.price}</div>
                <button className="text-[10px] font-black text-slate-400 hover:text-indigo-600 dark:hover:text-white uppercase transition-colors tracking-widest">Add</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Pricing;
